import { useMemo } from "react";
import { useIntl } from "react-intl";
import { ReportAPI } from "@ai360/core";
import { createSelector } from "reselect";
import { selectors as unitSelectors, unitNames } from "~/core/units";
import {
    getHighlightedNonFieldFeatures,
    getSelectedFieldsCustomerGuid,
} from "~/customer-data/selectors";
import { useAppSelector } from "~/store/hooks";
import RootState from "~/types/store";
import { IReportType } from "./interfaces";
import { IYieldByCategory } from "~/reports/data/model";
import { CropScope, useCropList, useCustomerCropScope } from "~/utils/crops/use-crop-list";
import { computeIsSelectable } from "~/utils/products/use-product-list";
import { messages as globalMessages } from "~/i18n-messages";

export interface ISelectorCropData {
    cropGuid?: string;
    cropId?: string;
    cropName?: string;
    activeYn?: boolean;
    isAgvanceLinked?: boolean | null;
    // Carried through only if the source sets it, same convention as prepareSelectableOptions
    // (admin/utils.ts) added so per-crop tooltips can be wired later without a signature change.
    getToolTip?: () => string;
}

export interface ISelectorCropDataReturn {
    value: {
        cropGuid: string;
    };
    label: string;
    activeYn: boolean;
    isSelectable: boolean;
    // Present whenever the source set it (see ISelectorCropData) — distinguishes "hide this
    // option" (isSelectable) from "the selected value itself is unlinked" (isAgvanceLinked ===
    // false), same convention as prepareSelectableOptions (admin/utils.ts).
    isAgvanceLinked?: boolean | null;
    getToolTip?: () => string;
}
export interface ISelectorParsedTotalingData {
    guid?: string;
    name?: string;
    id?: string;
}

export interface ISelectorParsedTotalingData {
    value: {
        id: string;
        guid: string;
        name: string;
    };
    label: string;
}

export const REPORTS_STATE_KEY = "REPORTS_STATE";

export const getFileNameFormatData = (state: unknown): any =>
    state[REPORTS_STATE_KEY].fileNameFormatList;

export const getReportData = (state: unknown): any => state[REPORTS_STATE_KEY].reportData;

export const getReportDataByReportType = (reportType: string) =>
    createSelector(
        (state: RootState) => state[REPORTS_STATE_KEY].reportData,
        (reportData) => reportData && reportData[reportType]
    );

export const getReportDataItem = (reportType: string, key: string) =>
    createSelector(
        getReportDataByReportType(reportType),
        (reportData) => reportData && reportData[key]
    );

export const getReportPref = (state: unknown): any => state[REPORTS_STATE_KEY].reportPreferences;

export const getReportTypes = (state: unknown): IReportType[] => state[REPORTS_STATE_KEY].typeList;

export const getReportTypePages = (state: unknown): any => state[REPORTS_STATE_KEY].pageList;

export const getIsTableLoading = (state: unknown): boolean =>
    state[REPORTS_STATE_KEY].isTableLoading;

export const getYieldByCompareByOptions = (state: unknown): IYieldByCategory =>
    state[REPORTS_STATE_KEY].yieldByCompareByOptions;

export const selectNonFieldFeaturePreference = (state: unknown): any =>
    state[REPORTS_STATE_KEY].defaultShowNonFieldFeature;

export const getParsedCropData = (cropData: ISelectorCropData[]): ISelectorCropDataReturn[] => {
    return cropData.map((crop) => {
        const { cropGuid, cropId, cropName, activeYn, isAgvanceLinked, getToolTip } = crop;
        return {
            value: {
                cropGuid,
                cropId,
            },
            label: cropName,
            activeYn,
            isSelectable: computeIsSelectable(activeYn, isAgvanceLinked ?? null),
            ...(isAgvanceLinked !== undefined && { isAgvanceLinked }),
            // Top level, sibling to value/label -- matches prepareSelectableOptions's convention
            // and SelectInput's option.getToolTip read (select-input.tsx).
            ...(getToolTip !== undefined && { getToolTip }),
        };
    });
};

/** The one crop scope every report crop surface shares (one cache entry): the selected fields' customer, else `Catalog`. */
export const useReportCropScope = (): CropScope =>
    useCustomerCropScope(useAppSelector(getSelectedFieldsCustomerGuid));

export const useReportCropOptions = (): ISelectorCropDataReturn[] => {
    const { crops: rawCrops } = useCropList({ scope: useReportCropScope() });
    const { formatMessage } = useIntl();
    return useMemo(
        () =>
            getParsedCropData(
                rawCrops.map((crop) => ({
                    cropGuid: crop.cropGuid,
                    cropId: String(crop.cropId),
                    cropName: crop.name,
                    activeYn: crop.activeYn,
                    isAgvanceLinked: crop.isAgvanceLinked,
                    ...(crop.isAgvanceLinked === false && {
                        getToolTip: () => formatMessage(globalMessages.cropNotLinkedInAgvance),
                    }),
                }))
            ),
        [rawCrops, formatMessage]
    );
};

export const getWeightUnitData = (state: unknown): ISelectorParsedTotalingData[] =>
    unitSelectors.getUnitPicklistFromCode(state, unitNames.getUnitCode(unitNames.UNIT_WEIGHT)) ||
    [];

export const getParsedTotalingData = (
    totalingData: ISelectorParsedTotalingData[]
): ISelectorParsedTotalingData[] => {
    return totalingData.map((item) => {
        const { guid, id, name } = item;
        return {
            value: {
                id,
                guid,
                name,
            },
            label: name,
        };
    });
};

export const getTotalingOptions = createSelector(getWeightUnitData, (totalingData) =>
    getParsedTotalingData(totalingData)
);

export const getReportStatus = (state: unknown): any => state[REPORTS_STATE_KEY].reportStatus;

export const getReportTableRefreshNonce = (state: unknown): number =>
    state[REPORTS_STATE_KEY].reportTableRefreshNonce ?? 0;

export const getFields = (state: unknown): ReportAPI.IField[] => state[REPORTS_STATE_KEY].fields;

export const getSelectedNonFieldFeaturesGuids = createSelector(
    getHighlightedNonFieldFeatures,
    (selectedNonFieldFeaturesGuids) => selectedNonFieldFeaturesGuids.map((f) => f.id)
);
